import { useEffect } from 'react';
import Button from '../shared/Button';
import TurnByTurnDirections from './TurnByTurnDirections';
import useTripStore from '../../context/TripStore';
import { formatTime } from '../../utils/timeHelpers';
import { formatDuration } from '../../utils/formatters';

const NavigationGuide = () => {
  const { itinerary, places, navigation, stopNavigation, goToNextStop, completeStop, setCurrentStopIndex } = useTripStore();
  const activePlan = itinerary.activePlan === 'a' ? itinerary.planA : itinerary.planB;

  const { isActive, currentStopIndex, completedStops } = navigation || {};
  const currentStop = activePlan?.stops?.[currentStopIndex];
  const nextStop = activePlan?.stops?.[currentStopIndex + 1];
  const isLastStop = currentStopIndex === (activePlan?.stops?.length || 0) - 1;
  const progress = activePlan?.stops?.length > 0 ? ((currentStopIndex + 1) / activePlan.stops.length) * 100 : 0;

  // Center map on current stop when navigation is active
  useEffect(() => {
    if (isActive && currentStop && window.backgroundMapInstance) {
      const place = places.find(p => p.name === currentStop.name || p.placeId === currentStop.placeId);
      if (place?.coordinates) {
        window.backgroundMapInstance.setCenter(place.coordinates);
        window.backgroundMapInstance.setZoom(15);
      }
    }
  }, [isActive, currentStopIndex, currentStop, places]);

  if (!activePlan || !activePlan.stops || activePlan.stops.length === 0) {
    return null;
  }

  // Don't show the "Start Navigation" prompt if navigation is not active
  // The "Start Trip" button in the header handles this
  if (!isActive) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-light text-gray-600">
            Stop {currentStopIndex + 1} of {activePlan.stops.length}
          </span>
          <Button variant="secondary" onClick={stopNavigation} className="text-xs py-1 px-2">
            Stop Navigation
          </Button>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5">
          <div 
            className="bg-gray-900 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Current stop - prominent display */}
      <div className="bg-gray-900 text-white p-4 rounded mb-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <div className="text-xs font-light text-gray-300 mb-1">Current Stop</div>
            <h3 className="text-lg font-light mb-1">{currentStop.name}</h3>
            <div className="text-xs font-light text-gray-300">
              {formatTime(currentStop.arrival_time)} - {formatTime(currentStop.departure_time)}
            </div>
          </div>
          <div className="text-2xl font-light">{currentStopIndex + 1}</div>
        </div>
        
        {currentStop.duration_reasoning && (
          <p className="text-xs font-light text-gray-300 mt-2">{currentStop.duration_reasoning}</p>
        )}

        <div className="mt-4 flex gap-2">
          <Button 
            variant="secondary" 
            onClick={() => completeStop(currentStopIndex)}
            className="flex-1 text-xs py-2"
            disabled={completedStops.includes(currentStopIndex)}
          >
            {completedStops.includes(currentStopIndex) ? '✓ Completed' : 'Mark Complete'}
          </Button>
          {!isLastStop && (
            <Button 
              onClick={goToNextStop}
              className="flex-1 text-xs py-2"
            >
              Next Stop →
            </Button>
          )}
        </div>
      </div>

      {/* Turn-by-turn directions */}
      <TurnByTurnDirections />

      {/* Next stop preview */}
      {nextStop && (
        <div className="bg-gray-50 border border-gray-200 p-3 rounded mb-4">
          <div className="text-xs font-light text-xs text-gray-600 mb-1">Next Stop</div>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-light">{nextStop.name}</div>
              <div className="text-xs font-light text-gray-500">
                Arrive at {formatTime(nextStop.arrival_time)}
              </div>
            </div>
            {currentStop.transport_to_next && (
              <div className="text-right">
                <div className="text-xs font-light text-gray-600">
                  {currentStop.transport_to_next.mode === 'walk' ? '🚶' : '🚇'} 
                  {' '}{formatDuration(currentStop.transport_to_next.duration_minutes)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* All stops list - compact view */}
      <div className="space-y-2">
        <div className="text-xs font-light uppercase tracking-wide text-gray-600 mb-2">Full Itinerary</div>
        {activePlan.stops.map((stop, index) => {
          const isCurrent = index === currentStopIndex;
          const isCompleted = completedStops.includes(index);
          
          return (
            <div 
              key={index}
              className={`
                flex items-center gap-3 p-2 rounded cursor-pointer transition-colors
                ${isCurrent ? 'bg-gray-900 text-white' : isCompleted ? 'bg-gray-100' : 'bg-white border border-gray-200'}
              `}
              onClick={() => {
                if (!isActive) return;
                setCurrentStopIndex(index);
              }}
            >
              <div className={`
                w-6 h-6 rounded-full flex items-center justify-center text-xs font-light flex-shrink-0
                ${isCurrent ? 'bg-white text-gray-900' : isCompleted ? 'bg-gray-300 text-gray-700' : 'border border-gray-300 text-gray-700'}
              `}>
                {isCompleted ? '✓' : index + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className={`
                  text-sm font-light truncate
                  ${isCurrent ? 'text-white' : isCompleted ? 'text-gray-500 line-through' : 'text-gray-900'}
                `}>
                  {stop.name}
                </div>
                <div className={`
                  text-xs font-light
                  ${isCurrent ? 'text-gray-300' : 'text-gray-500'}
                `}>
                  {formatTime(stop.arrival_time)} - {formatTime(stop.departure_time)}
                </div>
              </div>
              {isCurrent && (
                <div className="text-xs font-light text-gray-300">Current</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationGuide;

